// SKILLSMITH-LENS (design 2026-11-02 "Skillsmith as the remote control"; Andrei 2026-11-02:
// "a remote control into them without logging in"). Everything here is the platform super admin's
// alone: 404 to company owners, admins and members, and to agency operators who are no super
// admin. Nothing reaches a tenant surface, and nothing here changes a live skill, setting, key or
// pointer: fixes are proposals through the one door, or one of the one-click actions.
//
//   Fleet view (cross-client, from the hourly rollup; the companies the super admin operates):
//   POST /v1/operator/skillsmith/fleet                 one fleet tool (`fleet_overview`, …):
//                                                      counts, rates, ranks and ids; audited once
//   GET  /v1/operator/skillsmith/reports               the scheduled reports, newest first
//   GET  /v1/operator/skillsmith/reports/{report_id}   one report: its five fixed sections
//   POST /v1/operator/skillsmith/reports/run           make a report now (the schedule's twin)
//
//   One client's Skillsmith (Andrei: "work on client X"; it runs inside that company's scope,
//   its deep-dive tools read that company only, on that company's model key and budget, booked
//   as System usage (maintenance); every order audited with its text):
//   GET  /v1/operator/skillsmith/companies             the companies to pick, with their Skillsmith
//   GET  /v1/operator/skillsmith/companies/{company_id}/orders        the order history
//   POST /v1/operator/skillsmith/companies/{company_id}/orders        a question or an order:
//                                                      the bounded tool loop, an answer with evidence
//   POST /v1/operator/skillsmith/companies/{company_id}/skill-proposals   a skill proposal through
//                                                      the one door (`source = skillsmith`)
//   GET|POST /v1/operator/skillsmith/companies/{company_id}/standing-orders  SKILLSMITH-CONTROL:
//                                                      orders left to run once, weekly or monthly
//   POST …/standing-orders/{standing_order_id}/status  pause, resume or cancel one
//
//   Drafts and actions:
//   GET  /v1/operator/skillsmith/drafts                config proposals, builder tickets, action cards
//   POST /v1/operator/skillsmith/drafts                file one (nothing applied)
//   POST /v1/operator/skillsmith/drafts/{draft_id}/decide   accept or dismiss (an action card's
//                                                      accept is the one click that runs it)
//   POST /v1/operator/skillsmith/actions               one of the one-click actions, audited with
//                                                      `proposed_by: skillsmith`
//
//   ClawPanel (HMAC, as the release report):
//   POST /v1/aigentpanel/satellite-incident            a satellite incident (restart, OOM, disk,
//                                                      apply-baseline failed, gateway down)
import { z } from "zod";
import {
  FleetReportKind,
  FleetReportNarrative,
  SatelliteIncidentKind,
  SatelliteIncidentSeverity,
  SkillsmithActionKind,
  SkillsmithCadence,
  SkillsmithDraftKind,
  SkillsmithDraftState,
  SkillsmithOrderKind,
  SkillsmithOrderStatus,
  SkillsmithStandingStatus,
} from "../enums";
import { BoundedJson, Timestamp, TimeZone, Uuid, WallTime } from "../primitives";
import { defineRoute } from "./route";
import { SkillProposalEvidence, SkillProposalName } from "./skill-proposals";

const base = "/v1/operator/skillsmith";
const operator = { feature: "core", tag: "operator", access: "operator" } as const;
const panel = { feature: "core", tag: "operator", access: "public" } as const;

// Tools -----------------------------------------------------------------------------------------

/** The fleet tools: aggregates over the rollup and the control plane (no row-level client data). */
export const SKILLSMITH_FLEET_TOOLS = [
  "fleet_overview",
  "fleet_company",
  "fleet_failures",
  "fleet_alerts",
  "fleet_skills",
  "fleet_proposals",
  "fleet_host_errors",
  "fleet_satellites",
  "fleet_settings_current",
] as const;
export const SkillsmithFleetTool = z.enum(SKILLSMITH_FLEET_TOOLS);

/** The deep-dive tools: row-level detail of ONE company, only inside that company's order. */
export const SKILLSMITH_DEEP_TOOLS = [
  "company_overview",
  "company_runs",
  "run_detail",
  "run_costs",
  "tasks",
  "thread_messages",
  "kpis",
  "skills",
  "log_lines",
  // ONBOARDING-P4: the HQ dispatcher's notes on the client's messages (read and log only)
  "client_notes",
] as const;
export const SkillsmithDeepTool = z.enum(SKILLSMITH_DEEP_TOOLS);

export const SkillsmithPeriod = z.enum(["1d", "7d", "30d"]);
export type SkillsmithPeriod = z.infer<typeof SkillsmithPeriod>;

export const SkillsmithToolArgs = z
  .strictObject({
    period: SkillsmithPeriod.optional(),
    company_id: Uuid.optional(),
    de_membership_id: Uuid.optional(),
    run_id: Uuid.optional(),
    thread_id: Uuid.optional(),
    kind: z.string().trim().min(1).max(64).optional(),
    status: z.string().trim().min(1).max(32).optional(),
    since: Timestamp.optional(),
    until: Timestamp.optional(),
  })
  .meta({ id: "SkillsmithToolArgs" });
export type SkillsmithToolArgs = z.infer<typeof SkillsmithToolArgs>;

export const SkillsmithEvidenceRef = z
  .object({ kind: z.string().max(64), id: z.string().max(200) })
  .meta({ id: "SkillsmithEvidenceRef" });

export const SkillsmithFleetCall = z
  .strictObject({ tool: SkillsmithFleetTool, args: SkillsmithToolArgs.optional() })
  .meta({ id: "SkillsmithFleetCall" });

export const SkillsmithToolResult = z
  .object({
    tool: z.string(),
    /** The tool's rows (counts, rates, ids; text redacted). */
    data: BoundedJson,
    evidence: z.array(SkillsmithEvidenceRef),
    /** The audit row this read wrote (`system_agent.client_read`). */
    audit_id: Uuid,
  })
  .meta({ id: "SkillsmithToolResult" });
export type SkillsmithToolResult = z.infer<typeof SkillsmithToolResult>;

// Reports ---------------------------------------------------------------------------------------

/** The fixed sections, in this order, in every report. */
export const SKILLSMITH_SECTIONS = [
  "what_works",
  "what_doesnt",
  "top_issues",
  "proposed_fixes",
  "waiting_on_you",
] as const;
export const SkillsmithSection = z.enum(SKILLSMITH_SECTIONS);

export const SkillsmithReportListItem = z
  .object({
    id: Uuid,
    kind: FleetReportKind,
    period_key: z.string(),
    period_start: Timestamp,
    period_end: Timestamp,
    narrative_status: FleetReportNarrative,
    cost_micros: z.number().int(),
    mailed_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "SkillsmithReportListItem" });

export const SkillsmithReport = SkillsmithReportListItem.extend({
  /** The deterministic digest: per section its items, each with numbers and evidence ids. */
  digest: BoundedJson,
  /** One to three sentences per section; null when the validator refused it (see the digest). */
  narrative: z.record(z.string(), z.string()).nullable(),
  narrative_note: z.string().nullable(),
  model: z.string().nullable(),
}).meta({ id: "SkillsmithReport" });
export type SkillsmithReport = z.infer<typeof SkillsmithReport>;

export const SkillsmithReportRun = z
  .strictObject({ kind: FleetReportKind })
  .meta({ id: "SkillsmithReportRun" });

const ReportParams = z.object({ report_id: Uuid });

// One client's Skillsmith ------------------------------------------------------------------------

export const SkillsmithCompany = z
  .object({
    company_id: Uuid,
    name: z.string(),
    slug: z.string(),
    /** The company's Skillsmith (its system agent); null until first used (made then). */
    skillsmith_membership_id: Uuid.nullable(),
    open_alerts: z.number().int(),
    runs_7d: z.number().int(),
    failed_7d: z.number().int(),
  })
  .meta({ id: "SkillsmithCompany" });
export type SkillsmithCompany = z.infer<typeof SkillsmithCompany>;

export const SkillsmithOrderCreate = z
  .strictObject({
    text: z.string().trim().min(1).max(2000),
    /** `question` (cheap: the rollup first) or `order` (a deep dive, row-level). Default order. */
    kind: SkillsmithOrderKind.optional(),
  })
  .meta({ id: "SkillsmithOrderCreate" });

export const SkillsmithToolCallView = z
  .object({ tool: z.string(), args: BoundedJson, rows: z.number().int() })
  .meta({ id: "SkillsmithToolCallView" });

export const SkillsmithDraftView = z
  .object({
    id: Uuid,
    kind: SkillsmithDraftKind,
    state: SkillsmithDraftState,
    company_id: Uuid.nullable(),
    order_id: Uuid.nullable(),
    report_id: Uuid.nullable(),
    title: z.string(),
    body: BoundedJson,
    evidence: z.array(SkillsmithEvidenceRef),
    decided_at: Timestamp.nullable(),
    outcome: BoundedJson.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "SkillsmithDraftView" });
export type SkillsmithDraftView = z.infer<typeof SkillsmithDraftView>;

export const SkillsmithOrder = z
  .object({
    id: Uuid,
    company_id: Uuid,
    membership_id: Uuid,
    kind: SkillsmithOrderKind,
    status: SkillsmithOrderStatus,
    text: z.string(),
    answer: z.string().nullable(),
    evidence: z.array(SkillsmithEvidenceRef),
    tool_calls: z.array(SkillsmithToolCallView),
    model: z.string().nullable(),
    cost_micros: z.number().int(),
    /** Always `system_maintenance`: billed to the company as "System usage (maintenance)". */
    usage_kind: z.literal("system_maintenance"),
    /** Action cards and other drafts this answer filed (proposed; nothing applied). */
    drafts: z.array(SkillsmithDraftView),
    /** True when the answer came from a recent identical order (no new reads, no cost). */
    cached: z.boolean(),
    /** SKILLSMITH-CONTROL: the standing order this run came from (null: given in the chat). */
    standing_order_id: Uuid.nullable(),
    created_at: Timestamp,
    finished_at: Timestamp.nullable(),
  })
  .meta({ id: "SkillsmithOrder" });
export type SkillsmithOrder = z.infer<typeof SkillsmithOrder>;

// Standing orders (SKILLSMITH-CONTROL) ------------------------------------------------------------

/**
 * An order left with one client's Skillsmith: `once` (one run at `run_at`, default now), `weekly`
 * (an ISO `weekday` 1–7 at `at`) or `monthly` (`month_day` 1–28 at `at`), in `time_zone` (default:
 * the Skillsmith setting's). The worker runs it when due as the super admin who gave it, re-checked
 * at every run; each run is an order in the history, booked to the client as System usage
 * (maintenance).
 */
export const SkillsmithStandingOrderCreate = z
  .strictObject({
    text: z.string().trim().min(1).max(2000),
    kind: SkillsmithOrderKind.optional(),
    cadence: SkillsmithCadence,
    run_at: Timestamp.optional(),
    time_zone: TimeZone.optional(),
    at: WallTime.optional(),
    weekday: z.int().min(1).max(7).optional(),
    month_day: z.int().min(1).max(28).optional(),
  })
  .superRefine((v, ctx) => {
    const need = (k: "at" | "weekday" | "month_day", ok: boolean, message: string) => {
      if (!ok) ctx.addIssue({ code: "custom", path: [k], message });
    };
    if (v.cadence === "once") {
      need("at", v.at === undefined, "a run-once order takes run_at, not at");
      need("weekday", v.weekday === undefined, "only a weekly order takes a weekday");
      need("month_day", v.month_day === undefined, "only a monthly order takes a month_day");
    } else {
      if (v.run_at !== undefined)
        ctx.addIssue({ code: "custom", path: ["run_at"], message: "only a run-once order" });
      need("at", v.at !== undefined, "the time of day it runs (HH:MM)");
      if (v.cadence === "weekly") {
        need("weekday", v.weekday !== undefined, "the weekday it runs (1 = Monday … 7 = Sunday)");
        need("month_day", v.month_day === undefined, "only a monthly order takes a month_day");
      } else {
        need("month_day", v.month_day !== undefined, "the day of the month it runs (1–28)");
        need("weekday", v.weekday === undefined, "only a weekly order takes a weekday");
      }
    }
  })
  .meta({ id: "SkillsmithStandingOrderCreate" });
export type SkillsmithStandingOrderCreate = z.infer<typeof SkillsmithStandingOrderCreate>;

export const SkillsmithStandingOrderStatusChange = z
  .strictObject({ status: z.enum(["active", "paused", "cancelled"]) })
  .meta({ id: "SkillsmithStandingOrderStatusChange" });

export const SkillsmithStandingOrder = z
  .object({
    id: Uuid,
    company_id: Uuid,
    kind: SkillsmithOrderKind,
    text: z.string(),
    cadence: SkillsmithCadence,
    time_zone: z.string(),
    at: z.string().nullable(),
    weekday: z.number().int().nullable(),
    month_day: z.number().int().nullable(),
    status: SkillsmithStandingStatus,
    next_run_at: Timestamp.nullable(),
    last_run_at: Timestamp.nullable(),
    last_order_id: Uuid.nullable(),
    /** Why the last due run did not run (the giver is no super admin any more, the cap, …). */
    last_note: z.string().nullable(),
    runs: z.number().int(),
    /** Always `system_maintenance`: every run is billed to the client as System usage (maintenance). */
    usage_kind: z.literal("system_maintenance"),
    created_at: Timestamp,
  })
  .meta({ id: "SkillsmithStandingOrder" });
export type SkillsmithStandingOrder = z.infer<typeof SkillsmithStandingOrder>;

const CompanyIdParams = z.object({ company_id: Uuid });
const StandingOrderParams = z.object({ company_id: Uuid, standing_order_id: Uuid });

export const SkillsmithSkillProposalCreate = z
  .strictObject({
    /** The DE whose skill it is (the proposal is filed in its name, as the one door wants). */
    de_membership_id: Uuid,
    name: SkillProposalName,
    why: z.string().trim().min(1).max(2000),
    evidence: z.array(SkillProposalEvidence).min(1).max(10),
    draft: z.string().trim().min(1).max(100_000),
    outcome: z.string().trim().min(1).max(1000).optional(),
    expected_effect: z.string().trim().min(1).max(1000).optional(),
    order_id: Uuid.optional(),
  })
  .meta({ id: "SkillsmithSkillProposalCreate" });

export const SkillsmithSkillProposalFiled = z
  .object({ proposal_id: Uuid, status: z.literal("proposed") })
  .meta({ id: "SkillsmithSkillProposalFiled" });

// Drafts and actions ----------------------------------------------------------------------------

export const SkillsmithActionParams = z
  .strictObject({
    run_id: Uuid.optional(),
    de_membership_id: Uuid.optional(),
    recurring_work_id: Uuid.optional(),
    task_id: Uuid.optional(),
    alert_id: Uuid.optional(),
    job_id: Uuid.optional(),
    reason: z.string().trim().min(1).max(300).optional(),
  })
  .meta({ id: "SkillsmithActionParams" });

export const SkillsmithActionCreate = z
  .strictObject({
    action: SkillsmithActionKind,
    /** The company it acts in; absent only for `retry_operator_job` (a platform job). */
    company_id: Uuid.optional(),
    params: SkillsmithActionParams,
    /** The action card this click fires, when it came from one. */
    draft_id: Uuid.optional(),
  })
  .meta({ id: "SkillsmithActionCreate" });

export const SkillsmithActionResult = z
  .object({
    action: SkillsmithActionKind,
    company_id: Uuid.nullable(),
    ok: z.boolean(),
    detail: z.string(),
    audit_id: Uuid,
  })
  .meta({ id: "SkillsmithActionResult" });

export const SkillsmithDraftCreate = z
  .discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("config_proposal"),
      title: z.string().trim().min(1).max(200),
      company_id: Uuid.optional(),
      order_id: Uuid.optional(),
      report_id: Uuid.optional(),
      /** The setting: key, scope (`platform` | `company`), and the value proposed (old → new). */
      key: z.string().trim().min(1).max(64),
      scope: z.enum(["platform", "company"]),
      to: BoundedJson,
      note: z.string().trim().min(1).max(500),
      evidence: z.array(SkillsmithEvidenceRef).max(20).optional(),
    }),
    z.strictObject({
      kind: z.literal("builder_ticket"),
      title: z.string().trim().min(1).max(200),
      company_id: Uuid.optional(),
      order_id: Uuid.optional(),
      report_id: Uuid.optional(),
      body: z.string().trim().min(1).max(8000),
      run_ids: z.array(Uuid).max(20).optional(),
      evidence: z.array(SkillsmithEvidenceRef).max(20).optional(),
    }),
    z.strictObject({
      kind: z.literal("action"),
      title: z.string().trim().min(1).max(200),
      company_id: Uuid.optional(),
      order_id: Uuid.optional(),
      report_id: Uuid.optional(),
      action: SkillsmithActionKind,
      params: SkillsmithActionParams,
      why: z.string().trim().min(1).max(1000),
      evidence: z.array(SkillsmithEvidenceRef).max(20).optional(),
    }),
  ])
  .meta({ id: "SkillsmithDraftCreate" });

export const SkillsmithDraftListQuery = z.object({
  state: SkillsmithDraftState.optional(),
});

export const SkillsmithDraftDecide = z
  .strictObject({ decision: z.enum(["accept", "dismiss"]), note: z.string().max(500).optional() })
  .meta({ id: "SkillsmithDraftDecide" });

const DraftParams = z.object({ draft_id: Uuid });

// ClawPanel -------------------------------------------------------------------------------------

/**
 * What ClawPanel sends for a satellite incident (signed as every panel call: the
 * `X-AigentPanel-Signature` HMAC over the raw body). `incident_id` is the panel's own id: a resend
 * is the same row; a later report with `resolved_at` closes it. `detail` is one short line — never
 * a log, a body or a secret (it is redacted again on arrival).
 */
export const SatelliteIncidentReport = z
  .strictObject({
    node_id: z
      .string()
      .min(1)
      .max(128)
      .regex(/^[A-Za-z0-9._-]+$/),
    incident_id: z
      .string()
      .min(1)
      .max(128)
      .regex(/^[A-Za-z0-9._:-]+$/),
    kind: SatelliteIncidentKind,
    severity: SatelliteIncidentSeverity,
    container: z.string().trim().min(1).max(128).nullable().optional(),
    detail: z.string().trim().max(300).nullable().optional(),
    occurred_at: Timestamp,
    resolved_at: Timestamp.nullable().optional(),
  })
  .meta({ id: "SatelliteIncidentReport" });
export type SatelliteIncidentReport = z.infer<typeof SatelliteIncidentReport>;

export const SatelliteIncidentAck = z
  .object({ ok: z.literal(true), duplicate: z.boolean() })
  .meta({ id: "SatelliteIncidentAck" });

// Routes ----------------------------------------------------------------------------------------

export const skillsmithRoutes = {
  "operator.skillsmith.fleet": defineRoute({
    ...operator,
    id: "operator.skillsmith.fleet",
    method: "post",
    path: `${base}/fleet`,
    summary:
      "A platform super admin runs one fleet tool over the rollup (counts, rates, ranks and ids across the companies they operate; audited)",
    body: SkillsmithFleetCall,
    // A read: the POST carries the tool and its arguments; the audit row is the platform's.
    safe: true,
    response: { status: 200, schema: SkillsmithToolResult },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.skillsmith.reports.list": defineRoute({
    ...operator,
    id: "operator.skillsmith.reports.list",
    method: "get",
    path: `${base}/reports`,
    summary: "The scheduled fleet reports, newest first (super admins only)",
    response: { status: 200, schema: z.object({ data: z.array(SkillsmithReportListItem) }) },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillsmith.reports.get": defineRoute({
    ...operator,
    id: "operator.skillsmith.reports.get",
    method: "get",
    path: `${base}/reports/{report_id}`,
    summary: "One fleet report with its five fixed sections (super admins only)",
    params: ReportParams,
    response: { status: 200, schema: SkillsmithReport },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillsmith.reports.run": defineRoute({
    ...operator,
    id: "operator.skillsmith.reports.run",
    method: "post",
    path: `${base}/reports/run`,
    summary:
      "Make a fleet report now for the period that just ended (the schedule's twin; a period already made returns it)",
    body: SkillsmithReportRun,
    // Control-plane rows only (the report and its audit line): no company state, no event.
    safe: true,
    response: { status: 200, schema: SkillsmithReport },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.skillsmith.companies.list": defineRoute({
    ...operator,
    id: "operator.skillsmith.companies.list",
    method: "get",
    path: `${base}/companies`,
    summary:
      "The companies a super admin may pick for Skillsmith (the ones they operate), with each one's Skillsmith and its week in counts",
    response: { status: 200, schema: z.object({ data: z.array(SkillsmithCompany) }) },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillsmith.orders.list": defineRoute({
    ...operator,
    id: "operator.skillsmith.orders.list",
    method: "get",
    path: `${base}/companies/{company_id}/orders`,
    summary: "One company's Skillsmith order history (super admins only)",
    params: CompanyIdParams,
    response: { status: 200, schema: z.object({ data: z.array(SkillsmithOrder) }) },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillsmith.orders.create": defineRoute({
    ...operator,
    id: "operator.skillsmith.orders.create",
    method: "post",
    path: `${base}/companies/{company_id}/orders`,
    summary:
      "Give one company's Skillsmith a question or an order: its read-only tools on that company only, an answer with evidence, every read audited with the order's text",
    description:
      "Runs inside the company's scope (its tenant boundary, its model key or the gateway, its " +
      "Skillsmith's daily cap) and is booked to the company as System usage (maintenance). The " +
      "company's Skillsmith is made on first use. Nothing is changed: actions it suggests come " +
      "back as proposed action cards for the super admin to click.",
    params: CompanyIdParams,
    body: SkillsmithOrderCreate,
    // Operator-only rows and the audit lines; a model call with a known cost posts the System
    // usage (maintenance) line on the company's ledger.
    response: { status: 200, schema: SkillsmithOrder },
    errors: ["not_found", "conflict", "rate_limited", "validation_failed"],
    emits: ["cost.posted"],
  }),
  // SKILLSMITH-CONTROL: standing orders (once at a time, weekly, monthly) to one client's Skillsmith.
  "operator.skillsmith.standingOrders.list": defineRoute({
    ...operator,
    id: "operator.skillsmith.standingOrders.list",
    method: "get",
    path: `${base}/companies/{company_id}/standing-orders`,
    summary: "One company's Skillsmith standing orders (super admins only)",
    params: CompanyIdParams,
    response: { status: 200, schema: z.object({ data: z.array(SkillsmithStandingOrder) }) },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillsmith.standingOrders.create": defineRoute({
    ...operator,
    id: "operator.skillsmith.standingOrders.create",
    method: "post",
    path: `${base}/companies/{company_id}/standing-orders`,
    summary:
      "Leave an order with one company's Skillsmith: run once at a time, weekly or monthly, in that company only (audited)",
    description:
      "The worker runs it when due as the super admin who gave it, re-checked at every run (still a " +
      "super admin, still operating the company); a run that is not admitted is recorded and not " +
      "made. Each run is an order in the company's history, booked to it as System usage (maintenance).",
    params: CompanyIdParams,
    body: SkillsmithStandingOrderCreate,
    // A control-plane row and its audit line; the runs happen in the worker. No company event.
    safe: true,
    response: { status: 201, schema: SkillsmithStandingOrder },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillsmith.standingOrders.status": defineRoute({
    ...operator,
    id: "operator.skillsmith.standingOrders.status",
    method: "post",
    path: `${base}/companies/{company_id}/standing-orders/{standing_order_id}/status`,
    summary: "Pause, resume or cancel one standing order (super admins only; audited)",
    params: StandingOrderParams,
    body: SkillsmithStandingOrderStatusChange,
    // A control-plane row and its audit line; no company event.
    safe: true,
    response: { status: 200, schema: SkillsmithStandingOrder },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillsmith.skillProposals.create": defineRoute({
    ...operator,
    id: "operator.skillsmith.skillProposals.create",
    method: "post",
    path: `${base}/companies/{company_id}/skill-proposals`,
    summary:
      "File a skill proposal from Skillsmith's findings through the one door (`source = skillsmith`): queued for review, nothing installed",
    params: CompanyIdParams,
    body: SkillsmithSkillProposalCreate,
    // The operator-only queue and its audit line; no company state, no event (as decide).
    safe: true,
    response: { status: 201, schema: SkillsmithSkillProposalFiled },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillsmith.drafts.list": defineRoute({
    ...operator,
    id: "operator.skillsmith.drafts.list",
    method: "get",
    path: `${base}/drafts`,
    summary:
      "Skillsmith's drafts — config proposals, builder tickets, action cards (super admins only)",
    query: SkillsmithDraftListQuery,
    response: { status: 200, schema: z.object({ data: z.array(SkillsmithDraftView) }) },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillsmith.drafts.create": defineRoute({
    ...operator,
    id: "operator.skillsmith.drafts.create",
    method: "post",
    path: `${base}/drafts`,
    summary:
      "File a draft: a config proposal (Apply stays the super admin's in the console), a builder ticket, or an action card",
    body: SkillsmithDraftCreate,
    safe: true,
    response: { status: 201, schema: SkillsmithDraftView },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillsmith.drafts.decide": defineRoute({
    ...operator,
    id: "operator.skillsmith.drafts.decide",
    method: "post",
    path: `${base}/drafts/{draft_id}/decide`,
    summary:
      "Accept or dismiss a draft. Accepting a config proposal or a builder ticket records the decision (the setting is applied, the ticket queued, by their own pages); accepting an action card runs it (the one click)",
    params: DraftParams,
    body: SkillsmithDraftDecide,
    response: { status: 200, schema: SkillsmithDraftView },
    errors: ["not_found", "conflict", "forbidden", "validation_failed"],
    emits: [
      "recurring.paused",
      "recurring.resumed",
      "recurring_work.updated",
      "wakeup.requested",
      "digital_employee.resumed",
    ],
  }),
  "operator.skillsmith.actions.run": defineRoute({
    ...operator,
    id: "operator.skillsmith.actions.run",
    method: "post",
    path: `${base}/actions`,
    summary:
      "One of the one-click actions (stop a run, resume a DE, pause or resume a schedule, re-wake a task, acknowledge or resolve an alert, retry an operator job), run as the super admin and audited with proposed_by: skillsmith",
    body: SkillsmithActionCreate,
    response: { status: 200, schema: SkillsmithActionResult },
    errors: ["not_found", "conflict", "forbidden", "validation_failed", "run_state_conflict"],
    emits: [
      "recurring.paused",
      "recurring.resumed",
      "recurring_work.updated",
      "wakeup.requested",
      "digital_employee.resumed",
    ],
  }),
  "aigentpanel.satelliteIncident": defineRoute({
    ...panel,
    id: "aigentpanel.satelliteIncident",
    method: "post",
    path: "/v1/aigentpanel/satellite-incident",
    summary:
      "The panel reports a satellite incident — container restart, OOM, disk, apply-baseline failed, gateway down (HMAC; audited)",
    body: SatelliteIncidentReport,
    // A control-plane row and its audit line; no company state.
    safe: true,
    response: { status: 200, schema: SatelliteIncidentAck },
    errors: ["signature_invalid", "not_found"],
    emits: [],
  }),
} as const;
